import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import MonitorApp from "./App";
import { API_BASE } from "./apiBase";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255,255,255,0.96)",
  zIndex: 999999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  fontSize: "1.1rem",
  color: "#111",
};

const logoutButtonStyle: React.CSSProperties = {
  marginTop: "20px",
  padding: "10px 20px",
  fontSize: "16px",
  cursor: "pointer",
  backgroundColor: "#dc3545",
  color: "white",
  border: "none",
  borderRadius: "4px",
};

const AuthenticatedMonitor: React.FC = () => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, getIdTokenClaims } = useAuth0();
  const [isSuperuser, setIsSuperuser] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // trigger interactive login
      loginWithRedirect().catch((e) => console.warn("loginWithRedirect failed:", e));
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Check superuser role after authentication
  useEffect(() => {
    if (!isAuthenticated) {
      setIsSuperuser(null);
      return;
    }

    const checkRole = async () => {
      try {
        console.log("[Monitor] Checking superuser role...");

        // Get role from ID token (Auth0)
        console.log("[Monitor] Getting role from ID token...");
        const claims = await getIdTokenClaims();
        console.log("[Monitor] ID Token claims:", claims);

        const roles = claims?.["https://zeitgeist/roles"] as string[] | undefined;
        console.log("[Monitor] Extracted roles from token:", roles);

        const hasSuperuser = Array.isArray(roles) && roles.includes("superuser");
        console.log("[Monitor] Has 'superuser' role from token:", hasSuperuser);

        setIsSuperuser(hasSuperuser);

        if (!hasSuperuser) {
          console.warn("[Monitor] Access denied: user roles are", roles, "but 'superuser' required");
        } else {
          console.log("[Monitor] Access granted: user has superuser role");
        }
      } catch (e) {
        console.error("[Monitor] Failed to check role:", e);
        setIsSuperuser(false);
      }
    };

    checkRole();
  }, [isAuthenticated, getIdTokenClaims]);

  // Always render a blocking overlay while we're not yet authenticated or authorized.
  const showOverlay = !isAuthenticated || isSuperuser === null || isSuperuser === false;

  // Determine overlay message
  let overlayMessage = "Checking authentication…";
  if (!isAuthenticated && !isLoading) {
    overlayMessage = "Redirecting to login…";
  } else if (isAuthenticated && isSuperuser === null) {
    overlayMessage = "Checking authorization…";
  } else if (isAuthenticated && isSuperuser === false) {
    overlayMessage = "Access Denied: Superuser role required";
  }

  return (
    <>
      {showOverlay && (
        <div style={overlayStyle} aria-hidden={!showOverlay}>
          <div>{overlayMessage}</div>
          {isAuthenticated && isSuperuser === false && (
            <button
              style={logoutButtonStyle}
              onClick={() => {
                localStorage.clear();
                logout({ logoutParams: { returnTo: window.location.origin } });
              }}
            >
              Logout and try another account
            </button>
          )}
        </div>
      )}
      {isAuthenticated && isSuperuser && (
        <button
          style={{
            position: "fixed",
            top: "10px",
            right: "10px",
            zIndex: 1000000,
            padding: "8px 16px",
            backgroundColor: "#dc3545",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={() => {
            localStorage.clear();
            logout({ logoutParams: { returnTo: window.location.origin } });
          }}
        >
          Logout
        </button>
      )}
      {isAuthenticated && isSuperuser && <MonitorApp />}
    </>
  );
};

export default AuthenticatedMonitor;
